import { useRef } from "react";
import { useReactFlow, Handle, Position, NodeResizer } from "@xyflow/react";
import { Input, Select, Upload, Button, Spin } from "antd";
import { PictureOutlined, InboxOutlined, DeleteOutlined, CloseOutlined, FilePdfOutlined, SoundOutlined, LinkOutlined, CopyOutlined } from "@ant-design/icons";
import api from "../../../../../../core/axios/axiosInstance";
import NodeNameInput from "./NodeNameInput";

const { Option } = Select;
const { Dragger } = Upload;

const ACCEPT_MAP = {
  image: "image/*",
  video: "video/*",
  audio: "audio/*",
  document: ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt",
};

const MediaNode = ({ data, selected, id }) => {
  const { deleteElements } = useReactFlow();
  const uploadingRef = useRef(false);
  const dataRef = useRef(data);
  dataRef.current = data;

  const emit = (patch) => {
    dataRef.current.onChange?.({ ...dataRef.current, ...patch });
  };

  const handleUpload = async (file) => {
    if (uploadingRef.current) return false;
    uploadingRef.current = true;
    emit({ uploading: true });
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("mediaType", dataRef.current.mediaType || "image");
      const res = await api.post("/api/waba/automation/upload-media", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      emit({ url: res.data.url, fileName: file.name, uploading: false });
    } catch {
      emit({ uploading: false });
    } finally {
      uploadingRef.current = false;
    }
    return false;
  };

  const clearFile = async () => {
    const urlToDelete = dataRef.current.url;
    emit({ url: "", fileName: "" });
    if (urlToDelete) {
      try {
        await api.delete("/api/waba/automation/upload-media", { data: { url: urlToDelete } });
      } catch {
        // best-effort
      }
    }
  };

  return (
    <div
      style={{
        background: "#fff",
        border: `2px solid ${selected ? "#f59e0b" : "#e5e7eb"}`,
        borderRadius: 10,
        padding: 14,
        minWidth: 280,
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        width: "100%",
        height: "100%",
        boxSizing: "border-box",
      }}
    >
      <NodeResizer minWidth={240} minHeight={80} isVisible={selected} color="#f59e0b" />
      <Handle type="target" position={Position.Left} style={{ background: "#f59e0b" }} />

      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <PictureOutlined style={{ color: "#f59e0b", fontSize: 16 }} />
        <strong style={{ fontSize: 13 }}>Media</strong>
        <Button type="text" size="small" icon={<CopyOutlined />} onClick={() => data.onDuplicate?.()} style={{ marginLeft: "auto", color: "#9ca3af" }} />
        <Button type="text" size="small" icon={<CloseOutlined />} onClick={() => deleteElements({ nodes: [{ id }] })} style={{ color: "#9ca3af" }} />
      </div>

      {/* Media Type */}
      <label style={{ fontSize: 11, color: "#6b7280", display: "block", marginBottom: 4 }}>
        Media Type
      </label>
      <div className="nodrag" onPointerDown={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
        <NodeNameInput data={data} />
        <Select
          value={data.mediaType || "image"}
          onChange={(v) => emit({ mediaType: v, url: "", fileName: "" })}
          size="small"
          style={{ width: "100%", marginBottom: 8 }}
        >
          <Option value="image">Image</Option>
          <Option value="video">Video</Option>
          <Option value="audio">Audio</Option>
          <Option value="document">Document</Option>
        </Select>
      </div>

      {/* Upload area */}
      {data.url ? (
        <div style={{ marginBottom: 8, position: "relative" }}>
          {/* Image preview */}
          {(data.mediaType || "image") === "image" && (
            <div style={{ position: "relative", borderRadius: 6, overflow: "hidden", border: "1px solid #e5e7eb" }}>
              <img
                src={data.url}
                alt="preview"
                style={{ width: "100%", maxHeight: 140, objectFit: "cover", display: "block" }}
              />
              <Button
                type="text"
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={clearFile}
                style={{ position: "absolute", top: 4, right: 4, background: "rgba(255,255,255,0.85)", borderRadius: 4 }}
              />
            </div>
          )}
          {/* Video preview */}
          {data.mediaType === "video" && (
            <div style={{ position: "relative", borderRadius: 6, overflow: "hidden", border: "1px solid #e5e7eb" }}>
              <video
                src={data.url}
                style={{ width: "100%", maxHeight: 140, display: "block", background: "#000" }}
                controls
              />
              <Button
                type="text"
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={clearFile}
                style={{ position: "absolute", top: 4, right: 4, background: "rgba(255,255,255,0.85)", borderRadius: 4 }}
              />
            </div>
          )}
          {/* Audio preview */}
          {data.mediaType === "audio" && (
            <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 6, padding: "8px 10px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                <SoundOutlined style={{ color: "#6366f1", fontSize: 16 }} />
                <span style={{ fontSize: 11, color: "#374151", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {data.fileName || "Audio file"}
                </span>
                <Button type="text" size="small" danger icon={<DeleteOutlined />} onClick={clearFile} />
              </div>
              <audio src={data.url} controls style={{ width: "100%", height: 32 }} />
            </div>
          )}
          {/* Document preview */}
          {data.mediaType === "document" && (
            <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 6, padding: "8px 10px", display: "flex", alignItems: "center", gap: 6 }}>
              <FilePdfOutlined style={{ color: "#ef4444", fontSize: 20 }} />
              <span style={{ fontSize: 11, color: "#374151", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {data.fileName || "Document"}
              </span>
              <Button
                type="text"
                size="small"
                icon={<LinkOutlined />}
                onClick={() => window.open(data.url, "_blank")}
                style={{ color: "#3b82f6" }}
                title="Open document"
              />
              <Button type="text" size="small" danger icon={<DeleteOutlined />} onClick={clearFile} />
            </div>
          )}
        </div>
      ) : (
        <Dragger
          accept={ACCEPT_MAP[data.mediaType || "image"]}
          beforeUpload={handleUpload}
          showUploadList={false}
          disabled={!!data.uploading}
          style={{ marginBottom: 8 }}
        >
          {data.uploading ? (
            <div style={{ padding: "8px 0", textAlign: "center" }}>
              <Spin size="small" />
              <span style={{ fontSize: 11, marginLeft: 6, color: "#6b7280" }}>Uploading...</span>
            </div>
          ) : (
            <>
              <p className="ant-upload-drag-icon" style={{ marginBottom: 4 }}>
                <InboxOutlined style={{ fontSize: 24, color: "#f59e0b" }} />
              </p>
              <p style={{ fontSize: 11, color: "#6b7280", margin: 0 }}>
                Click or drag file to upload
              </p>
            </>
          )}
        </Dragger>
      )}

      {/* Caption */}
      <label style={{ fontSize: 11, color: "#6b7280", display: "block", marginBottom: 4 }}>
        Caption (Optional){" "}
        <span style={{ color: "#9ca3af" }}>{(data.caption || "").length}/1098</span>
      </label>
      <div className="nodrag" onPointerDown={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
        <Input.TextArea
          value={data.caption || ""}
          onChange={(e) => emit({ caption: e.target.value })}
          rows={2}
          maxLength={1098}
          placeholder="Enter text"
          style={{ fontSize: 12 }}
        />
      </div>

      <Handle type="source" position={Position.Right} style={{ background: "#f59e0b" }} />
    </div>
  );
};

export default MediaNode;
